import React, { useCallback, useEffect, useState } from "react";
import Loading from "../common/Loading";
import { ACCENT, PrimaryButton, SecondaryButton, errorMessage } from "./shared";
import { termsApi } from "./termsApi";

// Artist Studio > Rights & Legal > Terms & Disclosures (FSD 6.8.4): the documents the artist agreed to, which version and when,
// a viewer for each, and a printable copy of the contributor waiver template.

export const formatDate = (value) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

// The document text: blank lines separate paragraphs.
export function DocumentText({ text, testId }) {
  const paragraphs = String(text || "").split(/\n{2,}/);
  return (
    <div className="space-y-3 text-sm text-gray-800 leading-relaxed" data-testid={testId}>
      {paragraphs.map((p, i) => (
        <p key={i} className="whitespace-pre-line">{p}</p>
      ))}
    </div>
  );
}

const escapeHtml = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// "Download PDF": opens the document in a clean print view; choosing "Save as PDF" in the print dialog makes the file.
export function printDocument(doc) {
  const w = window.open("", "_blank");
  if (!w) return false;
  const body = String(doc.content || "").split(/\n{2,}/).map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br/>")}</p>`).join("");
  w.document.write(
    `<!doctype html><html><head><meta charset="utf-8"/><title>${escapeHtml(doc.title)}</title>` +
      `<style>body{font-family:Arial,Helvetica,sans-serif;max-width:720px;margin:40px auto;padding:0 24px;color:#111;line-height:1.6}h1{font-size:20px}p{margin:0 0 14px}</style>` +
      `</head><body><h1>${escapeHtml(doc.title)}</h1>${body}</body></html>`
  );
  w.document.close();
  w.focus();
  w.print();
  return true;
}

function ViewDialog({ code, onClose }) {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    termsApi.get(code).then(
      (d) => !cancelled && setDoc(d),
      (e) => !cancelled && setError(errorMessage(e, "We couldn't load this document. Please try again."))
    );
    return () => { cancelled = true; };
  }, [code]);

  return (
    <div className="fixed inset-0 z-50 flex [align-items:safe_center] justify-center overflow-y-auto bg-black/40 px-4 py-4" role="dialog" aria-modal="true" aria-labelledby="terms-view-title" data-testid="terms-dialog">
      <div className="my-8 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <h2 id="terms-view-title" className="text-xl font-bold text-gray-900">{doc?.title || "Document"}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-2xl leading-none text-gray-500">×</button>
        </div>
        {doc && <p className="mt-1 text-xs text-gray-500">Version {doc.version} · in effect from {formatDate(doc.effective_at)}</p>}
        <div className="mt-4 max-h-[55vh] overflow-y-auto rounded-xl border border-gray-200 bg-gray-50 p-4">
          {!doc && !error && <div className="py-8 flex justify-center"><Loading /></div>}
          {error && <p className="text-sm text-red-600" data-testid="terms-dialog-error">{error}</p>}
          {doc && <DocumentText text={doc.content} testId="terms-dialog-text" />}
        </div>
        <div className="mt-5 flex flex-wrap justify-end gap-3">
          {doc?.downloadable && (
            <SecondaryButton onClick={() => printDocument(doc)} data-testid="terms-download">Download PDF</SecondaryButton>
          )}
          <PrimaryButton onClick={onClose} data-testid="terms-close">Close</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function StatusChip({ doc }) {
  if (!doc.requires_acceptance) {
    return <span className="rounded-full bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1">Template</span>;
  }
  if (doc.accepted) {
    return <span className="rounded-full bg-emerald-50 text-emerald-800 text-xs font-semibold px-3 py-1">Accepted</span>;
  }
  return <span className="rounded-full bg-amber-50 text-amber-800 text-xs font-semibold px-3 py-1">Needs your acceptance</span>;
}

export default function TermsDisclosures() {
  const [state, setState] = useState({ loading: true, documents: [], error: "" });
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const data = await termsApi.list();
      setState({ loading: false, documents: data?.documents || [], error: "" });
    } catch (e) {
      setState({ loading: false, documents: [], error: errorMessage(e, "We couldn't load your terms. Please try again.") });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (state.loading) return <div className="py-16 flex justify-center"><Loading /></div>;
  if (state.error) {
    return (
      <div data-testid="terms-error">
        <p className="text-sm text-red-600">{state.error}</p>
        <button type="button" onClick={load} className="mt-2 text-sm font-semibold underline" style={{ color: ACCENT }}>Try again</button>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-xl border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
              <th className="px-5 py-3 font-semibold">Document</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold">Version</th>
              <th className="px-5 py-3 font-semibold">Accepted on</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {state.documents.map((doc) => (
              <tr key={doc.code} data-testid={`terms-row-${doc.code}`}>
                <td className="px-5 py-4">
                  <p className="font-semibold text-gray-900">{doc.title}</p>
                  {doc.summary && <p className="text-xs text-gray-500 mt-0.5">{doc.summary}</p>}
                </td>
                <td className="px-5 py-4 whitespace-nowrap"><StatusChip doc={doc} /></td>
                <td className="px-5 py-4 whitespace-nowrap text-gray-700">
                  {doc.version}
                  {doc.requires_acceptance && !doc.accepted && doc.accepted_version && (
                    <span className="block text-xs text-gray-500">You accepted {doc.accepted_version}</span>
                  )}
                </td>
                <td className="px-5 py-4 whitespace-nowrap text-gray-700">{doc.accepted && doc.accepted_at ? formatDate(doc.accepted_at) : "—"}</td>
                <td className="px-5 py-4 text-right whitespace-nowrap">
                  <button type="button" onClick={() => setViewing(doc.code)} className="font-semibold underline" style={{ color: ACCENT }} data-testid={`terms-view-${doc.code}`}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {viewing && <ViewDialog code={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}
